import { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import Card from './Components/Card';
import { CARD_DEFAULT_CONFIGS } from './Constants';
import style from '../../ListPresentation.module.scss';
import {
  getActiveTestimonialIndex,
  getImageWrapperStyles,
  getViewableTestimonials,
} from './Utils';
import { useAutoScrollOrRotation } from '../../hooks/useAutoScrollOrRotation';
import { LAYOUT_TYPE } from 'src/containers/AdditionalPage/constant';
import { useWidgetDndContextData } from 'src/context/WidgetDndContext';
import { useListPresentationContextData } from '../../context/ListPresentationContext';
import { ADMIN_WIDGETS_NAMES } from 'src/constants/widgets';
import { PRODUCT_TESTIMONIALS_CARD_DEFAULT_CONFIGS } from 'src/components/WidgetMaker/WidgetDnD/WidgetProperties/ProductTestimonials/ProductTestimonialsConfig';
import { useSSRSelector } from 'src/redux/ssrStore';
import axios from 'axios';
import { ENDPOINT } from 'src/config/endpoints';
import { getReviewDataStructure } from 'src/components/WidgetMaker/WidgetDnD/WidgetProperties/ProductTestimonials/utils';

const Testimonials = () => {
  const {
    widgetContextState: { isBuilder, activeWidgetId },
    widgetContextHelpers: {handleWidgetPropChange}
  } = useWidgetDndContextData();
  const storeData = useSSRSelector((state: any) => state.storeReducer.store);
  const data = useListPresentationContextData();
  const { isMobile, itemCountTotal, listPresentationConfig, cardFrameConfig } =
    data.state;
  const { sectionContainerRef } = data.refs;
  const { getConfigsAndUtils } = data.utilities;
  const {
    oneGridCellGeometry,
    showAutoRotation,
    columns,
    gridGapAxis,
    containerTracks,
    isLayoutCarousel,
    isTestimonialListingPage,
    carouselIndicatorConfigs,
    viewAllItemsConfig
  } = getConfigsAndUtils().generalValues;

  const isCarousel = listPresentationConfig?.layoutType === LAYOUT_TYPE.CAROUSEL;
  const dataOrder = cardFrameConfig?.childElementOrder || [];
  const isProductTestmonial = listPresentationConfig?.type === ADMIN_WIDGETS_NAMES.PRODUCT_TESTIMONIAL

  const testimonialsData = getViewableTestimonials({
    cardFrameConfig,
    isCarousel,
    isTestimonialListingPage,
    containerTracks,
    isProductTestmonial,
    viewAllItemsConfig
  });

  const totalItems = testimonialsData?.length;
  const CONFIG_ACC_TYPE = isProductTestmonial ? PRODUCT_TESTIMONIALS_CARD_DEFAULT_CONFIGS : CARD_DEFAULT_CONFIGS
  const config = CONFIG_ACC_TYPE[listPresentationConfig?.layoutName];

  async function getAllSelectedReviewData(selectedReviewIds: string[]) {
    try {
      const dataToSend = {
        store_id: storeData.store_id,
        num_list: [
          ...selectedReviewIds
        ]
      }
      const responseData = await axios.post(ENDPOINT.PRODUCT_TESTIMONIAL.getPTestimonialById, dataToSend);
      const reviewData = JSON.parse(responseData?.data?.data || {});
      const updatedReviewData = getReviewDataStructure({review_rating : reviewData});
      cardFrameConfig.testimonialsList = updatedReviewData;
      handleWidgetPropChange({
        listPresentation: listPresentationConfig,
        cardFrame: cardFrameConfig
      }, activeWidgetId)

    } catch (err) {
    }
  }

  useEffect(() => {
    const selectedReviewIds = cardFrameConfig.selectedReviewsId;
    if (selectedReviewIds?.length) {
      getAllSelectedReviewData(selectedReviewIds)
    }
  }, [isProductTestmonial])

  const { isAutoScrollEnabled, autoScrollTimer } = useAutoScrollOrRotation({
    listPresentationConfig,
  });

  const effectiveTestimonialsData = showAutoRotation
    ? testimonialsData.concat(testimonialsData.slice(0, columns))
    : testimonialsData;

  // used only in case of layout-4
  const activeTestimonialIdx = getActiveTestimonialIndex({
    carouselIndicatorConfigs,
    totalItems,
  });

  const allTestimonialCards = effectiveTestimonialsData.map((testimonialData, index) => {
    const testimonialIndex = index % totalItems;
    return (
      <Card
        key={testimonialData?.id || index}
        dataOrder={dataOrder}
        testimonialData={testimonialData}
        listPresentationConfig={listPresentationConfig}
        cardFrameConfig={cardFrameConfig}
        oneGridCellGeometry={oneGridCellGeometry}
        isBuilder={isBuilder}
        columns={columns}
        isProductTestimonial={isProductTestmonial}
        isMobile={isMobile}
        index={testimonialIndex}
        isActiveCard={testimonialIndex === activeTestimonialIdx}
        isLayoutCarousel = {isLayoutCarousel}
        imageWrapperStyles={getImageWrapperStyles(
          cardFrameConfig?.imageShape,
          testimonialIndex
        )}
        {...config}
      />
    );
  });

  if (isCarousel && isAutoScrollEnabled) {
    const groupWidth =
      oneGridCellGeometry.width * itemCountTotal + itemCountTotal * gridGapAxis.columnGap;
    const testimonialCardWithWrapper = (
      <div
        style={{
          gap: gridGapAxis.columnGap,
          maxWidth: groupWidth,
          width: groupWidth,
          paddingRight: gridGapAxis.columnGap,
          animationDuration: autoScrollTimer * Math.ceil(totalItems / columns) + 's',
        }}
        className={style.group}
      >
        {allTestimonialCards}
      </div>
    );

    return (
      <div
        style={{
          maxWidth: sectionContainerRef?.clientWidth,
        }}
        className={twMerge(style.carousel, 'tw-pb-[10px] tw-pt-[35px]')}
      >
        {testimonialCardWithWrapper}
        {testimonialCardWithWrapper}
        {testimonialCardWithWrapper}
      </div>
    );
  }

  return <>{allTestimonialCards}</>;
};

export default Testimonials;
